import { useState, type FormEvent } from 'react';
import { Upload, Trash2, Loader2 } from 'lucide-react';
import { announcementAPI, systemMediaAPI, type Announcement } from '../../services/api';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';
import toast from '../Toast';

interface AnnouncementFormProps {
  announcement: Announcement | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AnnouncementForm({ announcement, onClose, onSuccess }: AnnouncementFormProps) {
  const [form, setForm] = useState({
    type: (announcement?.type || 'info') as 'info' | 'warning',
    label: announcement?.label || '公告',
    title: announcement?.title || '',
    content: announcement?.content || '',
    linkText: announcement?.linkText || '',
    linkUrl: announcement?.linkUrl || '',
    bannerImageUrl: announcement?.bannerImageUrl || '',
    isVisible: announcement?.isVisible ?? true,
    isLoginRequired: announcement?.isLoginRequired ?? false,
    sortOrder: announcement?.sortOrder || 0,
  });
  const [bannerUploading, setBannerUploading] = useState(false);

  const handleBannerUpload = async (file: File) => {
    if (!file) return;
    setBannerUploading(true);
    try {
      const result = await systemMediaAPI.upload(file, 'announcement_banner');
      setForm((prev) => ({ ...prev, bannerImageUrl: result.storageKey }));
      toast.success('Banner 图已上传');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '上传失败');
    } finally {
      setBannerUploading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (announcement) {
        await announcementAPI.updateAnnouncement(announcement.id, form);
      } else {
        await announcementAPI.createAnnouncement(form);
      }
      onSuccess();
    } catch (error) {
      toast.error('保存失败');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">类型</label>
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as 'info' | 'warning' })}
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          >
            <option value="info">信息 (蓝色)</option>
            <option value="warning">警告 (黄色)</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">标签</label>
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="公告"
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
            required
          />
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">标题</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="输入公告标题"
          className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          required
        />
      </div>
      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">Banner 图（可选）</label>
        <p className="text-xs text-light-muted mb-2">上传后该公告将以无背景色的纯图片横幅展示，不显示文字内容</p>
        {form.bannerImageUrl ? (
          <div className="relative rounded-xl overflow-hidden border border-light-border">
            <img
              src={resolveBrowserMediaUrl(form.bannerImageUrl)}
              alt="banner 预览"
              className="w-full max-h-40 object-cover"
            />
            <button
              type="button"
              onClick={() => setForm({ ...form, bannerImageUrl: '' })}
              className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/50 text-white hover:bg-black/70 transition-colors"
              title="移除 banner 图"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center gap-2 w-full h-28 border-2 border-dashed border-light-border rounded-xl cursor-pointer hover:border-accent-blue/50 hover:bg-light-hover/30 transition-colors">
            {bannerUploading ? (
              <>
                <Loader2 className="w-5 h-5 text-light-muted animate-spin" />
                <span className="text-sm text-light-muted">上传中...</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 text-light-muted" />
                <span className="text-sm text-light-muted">点击上传 banner 图</span>
              </>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleBannerUpload(file);
                e.target.value = '';
              }}
            />
          </label>
        )}
      </div>
      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">内容</label>
        <textarea
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="输入公告内容"
          rows={3}
          className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text resize-none"
          required
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">链接文字</label>
          <input
            type="text"
            value={form.linkText}
            onChange={(e) => setForm({ ...form, linkText: e.target.value })}
            placeholder="查看详情"
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">链接地址</label>
          <input
            type="text"
            value={form.linkUrl}
            onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
            placeholder="https://..."
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">排序</label>
          <input
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          />
        </div>
        <div className="flex items-center">
          <label className="mt-0 flex items-center gap-2 rounded-xl border border-light-border bg-light-hover/30 px-4 py-3 sm:mt-6">
            <input
              type="checkbox"
              checked={form.isVisible}
              onChange={(e) => setForm({ ...form, isVisible: e.target.checked })}
              className="w-4 h-4 rounded border-light-border"
            />
            <span className="text-sm text-light-text">显示公告</span>
          </label>
        </div>
        <div className="flex items-center">
          <label className="mt-0 flex items-center gap-2 rounded-xl border border-light-border bg-light-hover/30 px-4 py-3 sm:mt-6">
            <input
              type="checkbox"
              checked={form.isLoginRequired}
              onChange={(e) => setForm({ ...form, isLoginRequired: e.target.checked })}
              className="w-4 h-4 rounded border-light-border"
            />
            <span className="text-sm text-light-text">登录后可见</span>
          </label>
        </div>
      </div>
      <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-2 text-light-muted transition-colors hover:text-light-text sm:w-auto"
        >
          取消
        </button>
        <button
          type="submit"
          className="w-full rounded-xl bg-accent-blue px-6 py-2 font-medium text-white transition-all hover:shadow-lg sm:w-auto"
        >
          保存
        </button>
      </div>
    </form>
  );
}
